import React from 'react';
import Table from '../../../commonComponents/Table';
import { useTheme } from '../../../context/ThemeContext';
import type { TableComponentProps } from '../../../commonComponents/Table/types';

/**
 * `page` — a screen's main table: taller viewport, rounded outer corners.
 * `nested` — a table inside a SectionCard: tighter header, no outer rounding.
 */
type TableVariant = 'page' | 'nested';

interface CreatorProgramTableProps<T> extends TableComponentProps<T> {
  variant?: TableVariant;
}

const VARIANTS: Record<TableVariant, Partial<TableComponentProps<any>>> = {
  page: {
    scroll: { x: true, y: 600 },
    thPadding: '15px 10px',
    firstChildBorderRadius: '8px',
    borderBottomLeftRight: true,
  },
  nested: {
    scroll: { x: true },
    thPadding: '12px 10px',
  },
};

/**
 * The shared `Table` with this feature's presentation applied.
 *
 * Every table in the Creator Program repeated the same ten style props plus the
 * dark/light wiring; they live here instead so a change lands everywhere at once.
 * Everything else passes straight through, so callers keep the shared Table's API —
 * any prop a caller sets overrides the variant default below.
 */
const CreatorProgramTable = <T extends Record<string, any> = any>({
  variant = 'page',
  ...props
}: CreatorProgramTableProps<T>) => {
  const { isDarkMode } = useTheme();

  return (
    <Table<T>
      pagination={false}
      headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
      removeVerticalBorders
      tdPadding="10px 10px"
      border="0"
      theme={isDarkMode ? 'dark' : 'light'}
      {...(VARIANTS[variant] as Partial<TableComponentProps<T>>)}
      {...props}
    />
  );
};

export default CreatorProgramTable;
